import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, Truck, ShieldCheck } from "lucide-react";
import DealerBrandLogo from "../components/brand/DealerBrandLogo";
import PublicLegalConsent from "../components/public/PublicLegalConsent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);

export default function GelangensConfirmPage() {
    const { token } = useParams();
    const [state, setState] = useState("loading"); // loading | ready | done | error
    const [gn, setGn] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [legalOk, setLegalOk] = useState(false);
    const [form, setForm] = useState({ confirmer_name: "", arrival_place: "", arrival_date: today() });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/public/gelangensnachweis/${token}`);
                setGn(data);
                const b = data.buyer || {};
                setForm((p) => ({
                    ...p,
                    confirmer_name: b.company_name || [b.first_name, b.last_name].filter(Boolean).join(" ") || "",
                }));
                setState(data.status === "confirmed" ? "done" : "ready");
            } catch (e) {
                setError(e.response?.data?.detail || "Link ungültig oder abgelaufen.");
                setState("error");
            }
        })();
    }, [token]);

    const submit = async () => {
        if (!form.confirmer_name.trim() || !form.arrival_place.trim() || !form.arrival_date) {
            setError("Bitte Name, Ort und Datum des Erhalts angeben."); return;
        }
        setBusy(true); setError("");
        try {
            const { data } = await axios.post(`${API}/public/gelangensnachweis/${token}/confirm`, form);
            setGn(data); setState("done");
        } catch (e) {
            setError(e.response?.data?.detail || "Bestätigung fehlgeschlagen.");
            setBusy(false);
        }
    };

    const veh = gn?.vehicle || {};
    const vehTitle = veh.title || [veh.brand, veh.model].filter(Boolean).join(" ") || "Fahrzeug";

    return (
        <div className="min-h-screen bg-[#09090B] text-white flex items-start justify-center p-4">
            <div className="w-full max-w-md mt-8" data-testid="gn-confirm-page">
                <div className="flex items-center gap-2 mb-5 text-white">
                    <DealerBrandLogo variant="dark" logoUrl={gn?.dealer_logo} companyName={gn?.dealer_name}
                        imgClassName="h-10 max-w-[200px]" textClassName="text-2xl" />
                </div>

                {state === "loading" && (
                    <div className="flex items-center gap-2 text-white/70"><Loader2 className="w-5 h-5 animate-spin" /> Lädt…</div>
                )}

                {state === "error" && (
                    <div className="bg-red-500/10 border border-red-500/40 rounded-sm p-4 text-red-200" data-testid="gn-error">{error}</div>
                )}

                {(state === "ready" || state === "done") && gn && (
                    <>
                        <h1 className="font-display text-2xl font-black mb-1">Gelangensbestätigung</h1>
                        <p className="text-sm text-white/60 mb-5">
                            Bitte bestätigen Sie den Erhalt des Fahrzeugs im Bestimmungsland (§ 17a UStDV) für
                            <b className="text-white/80"> {gn.dealer_name}</b>.
                        </p>

                        <div className="bg-white/5 border border-white/10 rounded-sm p-4 mb-5 space-y-2 text-sm" data-testid="gn-vehicle">
                            <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#FF8300]" /> <b>{vehTitle}</b></div>
                            {veh.vin && <div className="text-white/60">FIN: {veh.vin}</div>}
                            <div className="text-white/60">Bestimmungsland: <b className="text-white/80">{gn.destination_country || "—"}</b></div>
                            {gn.invoice_number && <div className="text-white/60">Rechnung: {gn.invoice_number}</div>}
                        </div>

                        {state === "done" ? (
                            <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-sm p-5 text-center" data-testid="gn-done">
                                <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                                <div className="font-display text-lg font-black">Erhalt bestätigt</div>
                                <p className="text-sm text-white/70 mt-2">
                                    Bestätigt durch <b>{gn.confirmer_name}</b>
                                    {gn.arrival_place ? <> in {gn.arrival_place}</> : null}
                                    {gn.arrival_date ? <> am {gn.arrival_date}</> : null}.
                                </p>
                                <p className="text-[11px] text-white/40 mt-3">
                                    Bei elektronischer Übermittlung ist keine Unterschrift erforderlich (§ 17a Abs. 2 Nr. 2 UStDV).
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div>
                                    <label htmlFor="gn-name" className="text-[11px] uppercase tracking-widest font-bold text-[#FF8300]">Name des Abnehmers / Vertreters</label>
                                    <input id="gn-name" value={form.confirmer_name} onChange={(e) => setForm({ ...form, confirmer_name: e.target.value })}
                                        className="mt-1 w-full bg-white/5 border border-white/15 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-[#FF8300]" data-testid="gn-name-input" />
                                </div>
                                <div>
                                    <label htmlFor="gn-place" className="text-[11px] uppercase tracking-widest font-bold text-[#FF8300]">Ort des Erhalts (Bestimmungsland)</label>
                                    <input id="gn-place" value={form.arrival_place} onChange={(e) => setForm({ ...form, arrival_place: e.target.value })} placeholder="z. B. Wien"
                                        className="mt-1 w-full bg-white/5 border border-white/15 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-[#FF8300]" data-testid="gn-place-input" />
                                </div>
                                <div>
                                    <label htmlFor="gn-date" className="text-[11px] uppercase tracking-widest font-bold text-[#FF8300]">Tag des Erhalts</label>
                                    <input id="gn-date" type="date" value={form.arrival_date} onChange={(e) => setForm({ ...form, arrival_date: e.target.value })}
                                        className="mt-1 w-full bg-white/5 border border-white/15 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-[#FF8300]" data-testid="gn-date-input" />
                                </div>
                                {error && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2" data-testid="gn-error">{error}</div>}
                                <PublicLegalConsent token={token} portal="gelangens" onAcceptedChange={setLegalOk} accent="#FF8300" />
                                <button type="button" onClick={submit} disabled={busy || !legalOk}
                                    className="w-full bg-[#FF8300] disabled:opacity-60 text-[#09090B] font-black uppercase tracking-wider rounded-sm py-3.5 inline-flex items-center justify-center gap-2 active:scale-95 transition-transform"
                                    data-testid="gn-confirm-btn">
                                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />} Erhalt verbindlich bestätigen
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
